import type { IdSpan, Span } from './span';

/**
 * All pairs of blocks whose intervals overlap (touching edges do not overlap). Each pair is
 * ordered by start (ties by id); pairs are listed in that order too.
 */
export function findOverlaps(blocks: readonly IdSpan[]): Array<readonly [string, string]> {
  const sorted = [...blocks].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin || (a.id < b.id ? -1 : 1),
  );
  const pairs: Array<readonly [string, string]> = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const first = sorted[i];
    if (first === undefined) continue;
    for (let j = i + 1; j < sorted.length; j += 1) {
      const second = sorted[j];
      if (second === undefined || second.startMin >= first.endMin) break;
      pairs.push([first.id, second.id]);
    }
  }
  return pairs;
}

/**
 * Whether the union of `others` fully covers `span` — the duplicate-creation guard (redesign
 * §2.1.4): the caller passes the existing blocks of the same project and status and skips
 * creating a fully covered block (a `noop` result, not an error). An empty span counts as
 * covered.
 */
export function coveredBy(span: Span, others: readonly Span[]): boolean {
  const sorted = [...others].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  let cursor = span.startMin;
  for (const other of sorted) {
    if (other.startMin > cursor) break;
    cursor = Math.max(cursor, other.endMin);
  }
  return cursor >= span.endMin;
}
